import { memo, Suspense, useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation, useRoutes } from 'react-router-dom'
import { USER_TOKEN_COOKIE_NAME, VITE_BASE_URL } from 'config'
import { io } from 'socket.io-client'
import myAxios from 'service/axios'
import Cookies from 'js-cookie'

// Stable per-visitor id so multiple tabs of the same person are counted once by the
// backend online-users presence tracker.
const VISITOR_ID_KEY = '$flakon$.$uz$visitor$id$'

function getVisitorId(): string {
  try {
    let id: string | null = localStorage.getItem(VISITOR_ID_KEY)
    if (!id) {
      id = typeof crypto !== 'undefined' && typeof crypto?.randomUUID === 'function' ? crypto.randomUUID() : `v-${Date.now()}-${Math.random().toString(36).slice(2)}`
      localStorage.setItem(VISITOR_ID_KEY, id)
    }
    return id
  } catch {
    return `v-${Date.now()}-${Math.random().toString(36).slice(2)}`
  }
}

// Connecting marks this visitor as "online"; the connection lives for the page lifetime
// and the backend decrements the count automatically when the socket disconnects.
const socket = io(VITE_BASE_URL, {
  transports: ['websocket', 'polling'],
  withCredentials: true,
  auth: { clientType: 'client', visitorId: getVisitorId() },
})

// Redux store
import { useDispatch } from 'react-redux'
import { changeLoading, changeSettings } from 'store/store'

// Routes list
import generateRoutes from './routesLinks'

// Custom hooks
import { useTypedSelector } from 'hooks'

// Components
import { Loader } from 'components'

// Helpers
import { checkTokenValidity } from 'helpers'

// Locale languages
import i18n from 'i18next'
import i18next from 'i18next'
import { initReactI18next } from 'react-i18next'

// Importing Locale translated texts
import TranslationEN from 'Locale/En'
import TranslationRU from 'Locale/Ru'
import TranslationUZ from 'Locale/Uz'

i18n.use(initReactI18next).init({
  resources: {
    en: { translation: TranslationEN },
    ru: { translation: TranslationRU },
    uz: { translation: TranslationUZ },
  },
  lng: localStorage.getItem('$flakon$.$uz$language$') || 'ru',
  fallbackLng: localStorage.getItem('$flakon$.$uz$language$') || 'ru',
  interpolation: { escapeValue: false },
})

// Constants
import { localeSettings } from '../constants'

const Router = memo((): React.JSX.Element => {
  const params: URLSearchParams = useMemo(() => new URLSearchParams(window.location.search), [window.location.search])
  const pathname = useLocation().pathname
  const dispatch = useDispatch()

  const [loading, setLoading] = useState<boolean>(true)

  const {
    settings,
    isLoading,
    user: { isAuth, isAccountingAllowed, isOrderingAllowed, role },
  } = useTypedSelector((s) => s.store)

  const routes = useRoutes(generateRoutes(isAuth, isAccountingAllowed, isOrderingAllowed, role))

  const changeLoader = useCallback((param: boolean) => setLoading(param), [])

  const checking = useCallback(async () => {
    if (!Cookies.get(USER_TOKEN_COOKIE_NAME) && !params.get('telegram_bot')) {
      dispatch(changeLoading(false))
      setLoading(false)
      return
    }

    await checkTokenValidity(dispatch, changeLoader)

    const isFromTelegramBot: boolean = typeof window !== 'undefined' && typeof (window as any)?.TelegramWebviewProxy !== 'undefined' && pathname?.includes('/register')
    if (!isFromTelegramBot) {
      params.delete('telegram_bot')
      window.history.pushState({}, '', `?${params.toString()}`)
    }
  }, [params, dispatch, (window as any)?.TelegramWebviewProxy, pathname, window.history])

  const getSettings = useCallback(async () => {
    try {
      const response = await myAxios('/about')
      dispatch(changeSettings(response?.data))
    } catch {
      dispatch(changeSettings(localeSettings))
    }
  }, [dispatch, localeSettings])

  useEffect(() => {
    const currLang: boolean = params.get('lang')?.toLowerCase() === 'en' || params.get('lang')?.toLowerCase() === 'ru' || params.get('lang')?.toLowerCase() === 'uz'
    if (!localStorage.getItem('$flakon$.$uz$language$') && !currLang) localStorage.setItem('$flakon$.$uz$language$', 'ru')

    if (currLang) {
      i18next?.changeLanguage(params.get('lang')?.toLowerCase() || 'ru')
      localStorage.setItem('$flakon$.$uz$language$', params.get('lang')?.toLowerCase() || 'ru')
      document.documentElement.lang = params.get('lang')?.toLowerCase() || 'ru'
    }

    if (params.get('lang')) {
      params.delete('lang')
      window.history.pushState({}, '', `?${params.toString()}`)
    }

    if ((settings && settings?.id === 'locale-settings') || !settings || !settings.id) getSettings()
  }, [])

  // Checking user token validity
  useEffect(() => {
    if (navigator?.onLine) checking()

    // Ensure the presence socket is connected (it auto-connects, but reconnect after offline)
    if (!socket?.connected) socket?.connect()
  }, [])

  return (
    <Suspense fallback={<Loader />}>
      {!loading && routes}
      {isLoading && <Loader full={!isAuth} />}
    </Suspense>
  )
})

export default Router